import React from "react";

/**
 * @param {{ suggestions: any, onApply?: function, disabled?: boolean }} props
 */
export function ListingAssistantPanel({
  suggestions,
  onApply,
  disabled,
  onGenerateAi,
  aiLoading,
  aiError,
}) {
  const hasSuggestions = suggestions && suggestions.title;

  function handleApply(which) {
    if (!hasSuggestions || !onApply) return;
    const payload = {};
    if (which === "all" || which === "title") payload.title = suggestions.title;
    if (which === "all" || which === "short") payload.shortDescription = suggestions.shortDescription;
    if (which === "all" || which === "long") payload.longDescription = suggestions.longDescription;
    if (which === "all" || which === "tags") payload.tags = suggestions.tags;
    onApply(payload);
  }

  return (
    <div className="mudo-card p-4 text-sm space-y-3">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="mudo-section-title">Listing assistant</h3>
        <span className="text-[11px] opacity-70">Mudo draft text</span>
      </div>

      {!hasSuggestions ? (
        <p className="text-xs opacity-70">
          Start by filling in the basic details (name, category, clay, glaze, size). Mudo will draft a title,
          descriptions and tags for you here.
        </p>
      ) : (
        <>
          <div className="space-y-2 text-xs">
            <div>
              <div className="opacity-70 mb-0.5">Suggested title</div>
              <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm">
                {suggestions.title}
              </div>
            </div>

            <div>
              <div className="opacity-70 mb-0.5">Short description</div>
              <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2">
                {suggestions.shortDescription}
              </div>
            </div>

            <div>
              <div className="opacity-70 mb-0.5">Long description</div>
              <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 max-h-32 overflow-auto">
                {suggestions.longDescription}
              </div>
            </div>

            {suggestions.tags && suggestions.tags.length > 0 && (
              <div>
                <div className="opacity-70 mb-0.5">Suggested tags</div>
                <div className="flex flex-wrap gap-1 text-[11px]">
                  {suggestions.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-full border border-white/10 bg-black/40"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {suggestions.notes && suggestions.notes.length > 0 && (
            <ul className="text-[11px] opacity-80 space-y-1 list-disc list-inside">
              {suggestions.notes.map((n, idx) => (
                <li key={idx}>{n}</li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => handleApply("all")}
              disabled={disabled}
              className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply all
            </button>
            <button
              type="button"
              onClick={() => handleApply("title")}
              disabled={disabled}
              className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply title
            </button>
            <button
              type="button"
              onClick={() => handleApply("short")}
              disabled={disabled}
              className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply short desc
            </button>
            <button
              type="button"
              onClick={() => handleApply("long")}
              disabled={disabled}
              className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply long desc
            </button>
            <button
              type="button"
              onClick={() => handleApply("tags")}
              disabled={disabled}
              className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply tags
            </button>
          </div>
        </>
      )}

      <div className="border border-dashed border-cyan-500/40 rounded-xl px-3 py-2 text-[11px] space-y-1">
        <div className="flex items-center justify-between">
          <span className="uppercase tracking-wide opacity-70">Mudo AI</span>
          <button
            type="button"
            onClick={onGenerateAi}
            disabled={disabled || aiLoading}
            className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {aiLoading ? "Thinking…" : "Generate with AI"}
          </button>
        </div>
        {aiError ? (
          <p className="opacity-80 text-red-300 mt-1">{aiError}</p>
        ) : (
          <p className="opacity-80 mt-1">
            Mudo AI rewrites your listing based on clay, glaze, size and use. You can still edit everything after applying it.
          </p>
        )}
      </div>
    </div>
  );
}
